import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Card,
    Heading,
    Text,
    Flex,
    Container,
    Table,
    Button,
    Icon,
    Box,
    Stack,
    Badge,
    Input,
    Textarea,
    IconButton,
    Spinner,
    Tag,
    HStack,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PlusIcon,
    DocumentIcon,
    ArrowsPointingOutIcon,
    TrashIcon,
    PencilIcon,
    BookOpenIcon,
} from '@heroicons/react/24/outline';
import { Field } from '@/components/ui/field';
import { Prose } from '@/components/ui/prose';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import {
    DialogRoot,
    DialogContent,
    DialogHeader,
    DialogBody,
    DialogTitle,
    DialogCloseTrigger,
} from '@/components/ui/dialog';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import KnowledgeBaseModal from './knowledge-base-modal';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useWorkspace from '@/lib/hooks/use-workspace';
import { useAuth } from '@/lib/hooks/use-auth';
import useAIContentRequests from '@/lib/hooks/use-ai-content-requests';
import getSportName from '@/utils/get-sport-name';
import api from '@/utils/api';
import { AIContentRequest, ContentType, BlockType } from '@/lib/types';
import ContentSearchModal from './content-search-modal';

// Type for field options lookup: referenceID -> (optionValue -> optionLabel)
type FieldOptionsMap = Map<string, Map<string, string>>;
// Type for field name lookup: referenceID -> fieldName
type FieldNameMap = Map<string, string>;

// Helper function to get the display value for selection fields
const getSelectionDisplayValue = (
    value: any,
    fieldOptionsMap: FieldOptionsMap,
    fieldRefId: string
): string => {
    const optionsMap = fieldOptionsMap.get(fieldRefId);
    if (!optionsMap) {
        return Array.isArray(value) ? value.join(', ') : String(value);
    }

    if (Array.isArray(value)) {
        return value
            .map((v) => {
                const strVal = String(v);
                return optionsMap.get(strVal) || strVal;
            })
            .join(', ');
    }
    const strVal = String(value);
    return optionsMap.get(strVal) || strVal;
};

interface ContentTypeWithWorkspace extends ContentType {
    workspace_content_type_id: number;
    workspace_content_type_reference_id: string;
}

interface SectionConfigField {
    field_id: number;
    field_type: number;
    field_name: string;
    sectionconfigs: string;
    workspace_name: string;
    content_name: string;
    reference_id: string;
    sport_type: string;
    sample_image_url: string;
}

interface ParsedSectionConfig {
    field_name: string;
    options: string[];
    section_config_key: string;
}

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // If less than 7 days ago, show relative date
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    // Otherwise show short date format
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
};

const getStatusBadge = (status: string | null | undefined) => {
    if (!status) {
        return (
            <Badge colorPalette="gray" size="xs">
                Pending
            </Badge>
        );
    }

    switch (status.toLowerCase()) {
        case 'completed':
            return (
                <Badge colorPalette="green" size="xs">
                    Completed
                </Badge>
            );
        case 'in_progress':
        case 'processing':
            return (
                <Badge colorPalette="blue" size="xs">
                    In Progress
                </Badge>
            );
        case 'failed':
        case 'error':
            return (
                <Badge colorPalette="red" size="xs">
                    Failed
                </Badge>
            );
        default:
            return (
                <Badge colorPalette="gray" size="xs">
                    {status}
                </Badge>
            );
    }
};

interface RequestRowProps {
    request: AIContentRequest;
    onDelete: (requestId: number) => void;
    onAdd: (request: AIContentRequest) => void;
    onEdit: (request: AIContentRequest) => void;
    isAdding?: boolean;
    isEditing?: boolean;
    fieldOptionsMap: FieldOptionsMap;
    fieldNameMap: FieldNameMap;
    showSources?: boolean;
}

const RequestTableRow = ({ request, onDelete, onAdd, onEdit, isAdding = false, isEditing = false, fieldOptionsMap, fieldNameMap, showSources = false }: RequestRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
    const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
    const [isContentPromptExpanded, setIsContentPromptExpanded] = useState(false);
    const [isImgPromptExpanded, setIsImgPromptExpanded] = useState(false);

    // Parse sources if it's a string, otherwise use as-is
    const sources = useMemo(() => {
        if (!request.sources) return null;
        if (typeof request.sources === 'string') {
            try {
                return JSON.parse(request.sources);
            } catch (e) {
                console.error('Failed to parse sources:', e);
                return null;
            }
        }
        return request.sources;
    }, [request.sources]);

    // Render content value - maps selection field values to their labels
    const renderContentValue = (key: string, content: any) => {
        const value = content?.value;
        if (value === undefined || value === null) return null;

        // Get the field name from the map, fallback to content.label or key
        const fieldLabel = fieldNameMap.get(key) || content?.label || key;

        // Check if this field has options (is a selection field)
        const fieldOptions = fieldOptionsMap.get(key);
        if (fieldOptions && fieldOptions.size > 0) {
            const displayValue = getSelectionDisplayValue(value, fieldOptionsMap, key);
            const strVal = String(value);
            // Only show if we found a mapping (display value is different from original)
            if (displayValue !== strVal) {
                return (
                    <Box key={key} mb={3}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
                            {fieldLabel}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            {displayValue}
                        </Text>
                    </Box>
                );
            }
        }

        // Render as HTML for rich text content
        const strVal = String(value);
        if (typeof value === 'string' && (value.includes('<') || value.includes('&'))) {
            return (
                <Box key={key}>
                    <Prose size="md">
                        <div dangerouslySetInnerHTML={{ __html: value }} />
                    </Prose>
                </Box>
            );
        }

        // Render as plain text
        return (
            <Box key={key} mb={3}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
                    {fieldLabel}
                </Text>
                <Text fontSize="sm" color="gray.600">
                    {strVal}
                </Text>
            </Box>
        );
    };

    return (
        <>
            <Table.Row
                _hover={{ bg: 'gray.50' }}
                transition="background-color 0.2s"
                cursor="pointer"
            >
                <Table.Cell py={3} px={4}>
                    <Flex alignItems="center" gap={2}>
                        <Box flex={1} minW={0}>
                            <Text
                                color="gray.900"
                                fontSize="sm"
                                fontWeight="semibold"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                            >
                                {request.name}
                            </Text>
                        </Box>
                        <IconButton
                            variant="ghost"
                            size="xs"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            colorPalette="gray"
                        >
                            <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} w={3.5} h={3.5} />
                        </IconButton>
                    </Flex>
                </Table.Cell>
                <Table.Cell py={3} px={4}>
                    <Stack gap={1.5}>
                        <Box>
                            {getStatusBadge(request.creation_result)}
                        </Box>
                        <Text color="gray.500" fontSize="xs" whiteSpace="nowrap">
                            {formatDate(request.created_at)}
                        </Text>
                    </Stack>
                </Table.Cell>
                <Table.Cell py={3} px={4} textAlign="right">
                    {request.creation_result === 'completed' ? (
                        <Flex gap={1} justify="flex-end" align="center">
                            <Tooltip content="Add to library">
                                <IconButton
                                    variant="ghost"
                                    size="xs"
                                    colorPalette="brand"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAdd(request);
                                    }}
                                    aria-label="Add to library"
                                    loading={isAdding}
                                    disabled={isAdding || isEditing}
                                >
                                    <Icon as={PlusIcon} w={4} h={4} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip content="Edit content">
                                <IconButton
                                    variant="ghost"
                                    size="xs"
                                    colorPalette="brand"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(request);
                                    }}
                                    aria-label="Edit content"
                                    loading={isEditing}
                                    disabled={isAdding || isEditing}
                                >
                                    <Icon as={PencilIcon} w={4} h={4} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip content="Delete request">
                                <IconButton
                                    variant="ghost"
                                    size="xs"
                                    colorPalette="red"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(request.id);
                                    }}
                                    aria-label="Delete request"
                                >
                                    <Icon as={TrashIcon} w={4} h={4} />
                                </IconButton>
                            </Tooltip>
                        </Flex>
                    ) : (
                        <Tooltip content="Delete request">
                            <IconButton
                                variant="ghost"
                                size="xs"
                                colorPalette="red"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(request.id);
                                }}
                                aria-label="Delete request"
                            >
                                <Icon as={TrashIcon} w={4} h={4} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Table.Cell>
            </Table.Row>
            {isExpanded && (
                <Table.Row>
                    <Table.Cell colSpan={3} py={0}>
                        <Box p={6} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                            {(() => {
                                // Use created_description if completed, otherwise use description
                                const description = request.creation_result === 'completed'
                                    ? (request as any).created_description || request.description
                                    : request.description;

                                return description ? (
                                    <Box mb={4}>
                                        <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                            Description
                                        </Text>
                                        <Text color="gray.700" fontSize="sm" lineHeight="relaxed">
                                            {description}
                                        </Text>
                                    </Box>
                                ) : null;
                            })()}
                            {(() => {
                                const createdImg = (request as any).created_img;
                                return createdImg && typeof createdImg === 'string' && createdImg.trim() ? (
                                    <Box mb={4}>
                                        <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                            Image
                                        </Text>
                                        <Box
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            bg="white"
                                            p={2}
                                            overflow="hidden"
                                            position="relative"
                                            cursor="pointer"
                                            _hover={{
                                                borderColor: 'brand.500',
                                                '& .enlarge-icon': {
                                                    opacity: 1,
                                                },
                                            }}
                                            onClick={() => setEnlargedImageUrl(createdImg)}
                                            transition="all 0.2s"
                                        >
                                            <img
                                                src={createdImg}
                                                alt="Generated content image"
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'block',
                                                    borderRadius: '6px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                            <Box
                                                position="absolute"
                                                top={2}
                                                right={2}
                                                bg="blackAlpha.700"
                                                borderRadius="md"
                                                p={1.5}
                                                className="enlarge-icon"
                                                opacity={0}
                                                transition="opacity 0.2s"
                                            >
                                                <Icon as={ArrowsPointingOutIcon} w={4} h={4} color="white" />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null;
                            })()}
                            {request.created_content && (
                                <>
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                        Generated Content
                                    </Text>
                                    <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                                        {Array.isArray(request.created_content) ? (
                                            request.created_content.map((content: any, index: number) => (
                                                renderContentValue(String(index), content)
                                            ))
                                        ) : typeof request.created_content === 'object' ? (
                                            Object.entries(request.created_content).map(([key, content]: [string, any]) => (
                                                renderContentValue(key, content)
                                            ))
                                        ) : (
                                            <Prose size="md">
                                                <div dangerouslySetInnerHTML={{ __html: request.created_content as string }} />
                                            </Prose>
                                        )}
                                    </Box>
                                </>
                            )}
                            {showSources && sources && (
                                <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setIsSourcesExpanded(!isSourcesExpanded)}
                                        color="blue.600"
                                        fontWeight="medium"
                                        px={0}
                                        _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                    >
                                        {isSourcesExpanded ? 'Hide Sources' : 'View Sources'}
                                        <Icon ml={1} boxSize={4}>
                                            {isSourcesExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </Icon>
                                    </Button>

                                    {isSourcesExpanded && (
                                        <Box mt={3}>
                                            {sources.synthesized_answer && (
                                                <Box mb={4}>
                                                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase">
                                                        Synthesized Answer
                                                    </Text>
                                                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                        {sources.synthesized_answer}
                                                    </Text>
                                                </Box>
                                            )}

                                            {sources.chunks && sources.chunks.length > 0 && (
                                                <Box mb={4}>
                                                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
                                                        Chunks ({sources.chunks.length})
                                                    </Text>
                                                    <Stack gap={2}>
                                                        {sources.chunks.map((chunk: string, index: number) => (
                                                            <Box key={index} p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                                                <Text fontSize="xs" color="gray.500" mb={1}>Chunk {index + 1}</Text>
                                                                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                                    {chunk}
                                                                </Text>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}

                                            {sources.documents && sources.documents.length > 0 && (
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
                                                        Documents ({sources.documents.length})
                                                    </Text>
                                                    <Stack gap={1}>
                                                        {sources.documents.map((document: string, index: number) => (
                                                            <Text key={index} fontSize="sm" color="gray.700">
                                                                • {document}
                                                            </Text>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {showSources && request.ai_prompt_content && (
                                <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setIsContentPromptExpanded(!isContentPromptExpanded)}
                                        color="blue.600"
                                        fontWeight="medium"
                                        px={0}
                                        _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                    >
                                        {isContentPromptExpanded ? 'Hide Content Prompt' : 'View Content Prompt'}
                                        <Icon ml={1} boxSize={4}>
                                            {isContentPromptExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </Icon>
                                    </Button>

                                    {isContentPromptExpanded && (
                                        <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                {request.ai_prompt_content}
                                            </Text>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {showSources && request.ai_prompt_image && (
                                <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setIsImgPromptExpanded(!isImgPromptExpanded)}
                                        color="blue.600"
                                        fontWeight="medium"
                                        px={0}
                                        _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                    >
                                        {isImgPromptExpanded ? 'Hide Image Prompt' : 'View Image Prompt'}
                                        <Icon ml={1} boxSize={4}>
                                            {isImgPromptExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </Icon>
                                    </Button>

                                    {isImgPromptExpanded && (
                                        <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                {request.ai_prompt_image}
                                            </Text>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {(() => {
                                const description = request.creation_result === 'completed'
                                    ? (request as any).created_description || request.description
                                    : request.description;
                                const createdImg = (request as any).created_img;
                                const hasPrompts = showSources && (request.ai_prompt_content || request.ai_prompt_image);

                                if (!description && !createdImg && !request.created_content && !(showSources && sources) && !hasPrompts) {
                                    return (
                                        <Text color="gray.400" fontSize="sm" fontStyle="italic">
                                            No additional details available
                                        </Text>
                                    );
                                }
                                return null;
                            })()}
                        </Box>
                    </Table.Cell>
                </Table.Row>
            )}

            {/* Image Enlarge Modal */}
            <DialogRoot open={!!enlargedImageUrl} onOpenChange={(e) => !e.open && setEnlargedImageUrl(null)} size="xl">
                <DialogContent maxW="90vw" maxH="90vh">
                    <DialogHeader>
                        <DialogTitle>Generated Image</DialogTitle>
                        <DialogCloseTrigger />
                    </DialogHeader>
                    <DialogBody p={0}>
                        {enlargedImageUrl && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={4}
                                bg="gray.50"
                                maxH="85vh"
                                overflow="auto"
                            >
                                <img
                                    src={enlargedImageUrl}
                                    alt="Generated content image (enlarged)"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '85vh',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        )}
                    </DialogBody>
                </DialogContent>
            </DialogRoot>
        </>
    );
};

const MobileRequestCard = ({ request, onDelete, onAdd, onEdit, isAdding = false, isEditing = false, fieldOptionsMap, fieldNameMap, showSources = false }: RequestRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
    const [isSourcesExpanded, setIsSourcesExpanded] = useState(false);
    const [isContentPromptExpanded, setIsContentPromptExpanded] = useState(false);
    const [isImgPromptExpanded, setIsImgPromptExpanded] = useState(false);

    // Parse sources if it's a string, otherwise use as-is
    const sources = useMemo(() => {
        if (!request.sources) return null;
        if (typeof request.sources === 'string') {
            try {
                return JSON.parse(request.sources);
            } catch (e) {
                console.error('Failed to parse sources:', e);
                return null;
            }
        }
        return request.sources;
    }, [request.sources]);

    // Render content value - maps selection field values to their labels
    const renderContentValue = (key: string, content: any) => {
        const value = content?.value;
        if (value === undefined || value === null) return null;

        // Get the field name from the map, fallback to content.label or key
        const fieldLabel = fieldNameMap.get(key) || content?.label || key;

        // Check if this field has options (is a selection field)
        const fieldOptions = fieldOptionsMap.get(key);
        if (fieldOptions && fieldOptions.size > 0) {
            const displayValue = getSelectionDisplayValue(value, fieldOptionsMap, key);
            const strVal = String(value);
            // Only show if we found a mapping (display value is different from original)
            if (displayValue !== strVal) {
                return (
                    <Box key={key} mb={3}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
                            {fieldLabel}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                            {displayValue}
                        </Text>
                    </Box>
                );
            }
        }

        // Render as HTML for rich text content
        const strVal = String(value);
        if (typeof value === 'string' && (value.includes('<') || value.includes('&'))) {
            return (
                <Box key={key}>
                    <Prose size="md">
                        <div dangerouslySetInnerHTML={{ __html: value }} />
                    </Prose>
                </Box>
            );
        }

        // Render as plain text
        return (
            <Box key={key} mb={3}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
                    {fieldLabel}
                </Text>
                <Text fontSize="sm" color="gray.600">
                    {strVal}
                </Text>
            </Box>
        );
    };

    return (
        <Card.Root>
            <Card.Body p={5}>
                <Stack gap={4}>
                    <Flex justify="space-between" align="flex-start" gap={3}>
                        <Box flex={1} minW={0}>
                            <Text color="gray.900" fontSize="md" fontWeight="semibold" mb={1}>
                                {request.name}
                            </Text>
                        </Box>
                        {getStatusBadge(request.creation_result)}
                    </Flex>

                    <Flex justify="space-between" align="center" gap={2} pt={2} borderTop="1px solid" borderColor="gray.100">
                        <Text color="gray.500" fontSize="xs" fontWeight="medium">
                            {formatDate(request.created_at)}
                        </Text>
                        {request.creation_result === 'completed' ? (
                            <Flex gap={1} align="center">
                                <Tooltip content="Add to library">
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        colorPalette="brand"
                                        onClick={() => onAdd(request)}
                                        aria-label="Add to library"
                                        loading={isAdding}
                                        disabled={isAdding || isEditing}
                                    >
                                        <Icon as={PlusIcon} w={4} h={4} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip content="Edit content">
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        colorPalette="brand"
                                        onClick={() => onEdit(request)}
                                        aria-label="Edit content"
                                        loading={isEditing}
                                        disabled={isAdding || isEditing}
                                    >
                                        <Icon as={PencilIcon} w={4} h={4} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip content="Delete request">
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        colorPalette="red"
                                        onClick={() => onDelete(request.id)}
                                        aria-label="Delete request"
                                        disabled={isAdding || isEditing}
                                    >
                                        <Icon as={TrashIcon} w={4} h={4} />
                                    </IconButton>
                                </Tooltip>
                            </Flex>
                        ) : (
                            <Tooltip content="Delete request">
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    colorPalette="red"
                                    onClick={() => onDelete(request.id)}
                                    aria-label="Delete request"
                                >
                                    <Icon as={TrashIcon} w={4} h={4} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Flex>

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsExpanded(!isExpanded)}
                        alignSelf="flex-start"
                        colorPalette="gray"
                    >
                        <Icon as={isExpanded ? ChevronUpIcon : ChevronDownIcon} w={4} h={4} mr={1} />
                        {isExpanded ? 'Hide' : 'Show'} Details
                    </Button>
                </Stack>

                {isExpanded && (
                    <Box mt={4} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                        {(() => {
                            // Use created_description if completed, otherwise use description
                            const description = request.creation_result === 'completed'
                                ? (request as any).created_description || request.description
                                : request.description;

                            return description ? (
                                <Box mb={4}>
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                        Description
                                    </Text>
                                    <Text color="gray.700" fontSize="sm" lineHeight="relaxed">
                                        {description}
                                    </Text>
                                </Box>
                            ) : null;
                        })()}
                        {(() => {
                            const createdImg = (request as any).created_img;
                            return createdImg && typeof createdImg === 'string' && createdImg.trim() ? (
                                <Box mb={4}>
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                        Image
                                    </Text>
                                    <Box
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        bg="white"
                                        p={2}
                                        overflow="hidden"
                                        position="relative"
                                        cursor="pointer"
                                        _hover={{
                                            borderColor: 'brand.500',
                                            '& .enlarge-icon': {
                                                opacity: 1,
                                            },
                                        }}
                                        onClick={() => setEnlargedImageUrl(createdImg)}
                                        transition="all 0.2s"
                                    >
                                        <img
                                            src={createdImg}
                                            alt="Generated content image"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                borderRadius: '6px',
                                                objectFit: 'contain',
                                            }}
                                        />
                                        <Box
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            bg="blackAlpha.700"
                                            borderRadius="md"
                                            p={1.5}
                                            className="enlarge-icon"
                                            opacity={0}
                                            transition="opacity 0.2s"
                                        >
                                            <Icon as={ArrowsPointingOutIcon} w={4} h={4} color="white" />
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null;
                        })()}
                        {request.created_content && (
                            <>
                                <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                    Generated Content
                                </Text>
                                <Box bg="white" p={4} borderRadius="md">
                                    {Array.isArray(request.created_content) ? (
                                        request.created_content.map((content: any, index: number) => (
                                            renderContentValue(String(index), content)
                                        ))
                                    ) : typeof request.created_content === 'object' ? (
                                        Object.entries(request.created_content).map(([key, content]: [string, any]) => (
                                            renderContentValue(key, content)
                                        ))
                                    ) : (
                                        <Prose size="md">
                                            <div dangerouslySetInnerHTML={{ __html: request.created_content as string }} />
                                        </Prose>
                                    )}
                                </Box>
                            </>
                        )}
                        {showSources && sources && (
                            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setIsSourcesExpanded(!isSourcesExpanded)}
                                    color="blue.600"
                                    fontWeight="medium"
                                    px={0}
                                    _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                >
                                    {isSourcesExpanded ? 'Hide Sources' : 'View Sources'}
                                    <Icon ml={1} boxSize={4}>
                                        {isSourcesExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Icon>
                                </Button>

                                {isSourcesExpanded && (
                                    <Box mt={3}>
                                        {sources.synthesized_answer && (
                                            <Box mb={4}>
                                                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase">
                                                    Synthesized Answer
                                                </Text>
                                                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                    {sources.synthesized_answer}
                                                </Text>
                                            </Box>
                                        )}

                                        {sources.chunks && sources.chunks.length > 0 && (
                                            <Box mb={4}>
                                                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
                                                    Chunks ({sources.chunks.length})
                                                </Text>
                                                <Stack gap={2}>
                                                    {sources.chunks.map((chunk: string, index: number) => (
                                                        <Box key={index} p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                                            <Text fontSize="xs" color="gray.500" mb={1}>Chunk {index + 1}</Text>
                                                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                                                {chunk}
                                                            </Text>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        {sources.documents && sources.documents.length > 0 && (
                                            <Box>
                                                <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2} textTransform="uppercase">
                                                    Documents ({sources.documents.length})
                                                </Text>
                                                <Stack gap={1}>
                                                    {sources.documents.map((document: string, index: number) => (
                                                        <Text key={index} fontSize="sm" color="gray.700">
                                                            • {document}
                                                        </Text>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                        {showSources && request.ai_prompt_content && (
                            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setIsContentPromptExpanded(!isContentPromptExpanded)}
                                    color="blue.600"
                                    fontWeight="medium"
                                    px={0}
                                    _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                >
                                    {isContentPromptExpanded ? 'Hide Content Prompt' : 'View Content Prompt'}
                                    <Icon ml={1} boxSize={4}>
                                        {isContentPromptExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Icon>
                                </Button>

                                {isContentPromptExpanded && (
                                    <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                        <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                            {request.ai_prompt_content}
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {showSources && request.ai_prompt_image && (
                            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setIsImgPromptExpanded(!isImgPromptExpanded)}
                                    color="blue.600"
                                    fontWeight="medium"
                                    px={0}
                                    _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                                >
                                    {isImgPromptExpanded ? 'Hide Image Prompt' : 'View Image Prompt'}
                                    <Icon ml={1} boxSize={4}>
                                        {isImgPromptExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                    </Icon>
                                </Button>

                                {isImgPromptExpanded && (
                                    <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                                        <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                                            {request.ai_prompt_image}
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        )}
                        {(() => {
                            const description = request.creation_result === 'completed'
                                ? (request as any).created_description || request.description
                                : request.description;
                            const createdImg = (request as any).created_img;
                            const hasPrompts = showSources && (request.ai_prompt_content || request.ai_prompt_image);

                            if (!description && !createdImg && !request.created_content && !(showSources && sources) && !hasPrompts) {
                                return (
                                    <Text color="gray.400" fontSize="sm" fontStyle="italic">
                                        No additional details available
                                    </Text>
                                );
                            }
                            return null;
                        })()}
                    </Box>
                )}

                {/* Image Enlarge Modal */}
                <DialogRoot open={!!enlargedImageUrl} onOpenChange={(e) => !e.open && setEnlargedImageUrl(null)} size="xl">
                    <DialogContent maxW="90vw" maxH="90vh">
                        <DialogHeader>
                            <DialogTitle>Generated Image</DialogTitle>
                            <DialogCloseTrigger />
                        </DialogHeader>
                        <DialogBody p={0}>
                            {enlargedImageUrl && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    p={4}
                                    bg="gray.50"
                                    maxH="85vh"
                                    overflow="auto"
                                >
                                    <img
                                        src={enlargedImageUrl}
                                        alt="Generated content image (enlarged)"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '85vh',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            objectFit: 'contain',
                                        }}
                                    />
                                </Box>
                            )}
                        </DialogBody>
                    </DialogContent>
                </DialogRoot>
            </Card.Body>
        </Card.Root>
    );
};

export default function AICreate() {
    const { workspaceId } = useParams<{ workspaceId: string }>();
    const navigate = useNavigate();
    const isAdmin = useIsInAdminGroup();
    const isCancelled = useIsCancelled();
    const { workspace } = useWorkspace();
    const { user } = useAuth();

    // Check if user is from athlete-era.com domain (show sources only for internal users)
    const showSources = useMemo(() => {
        return user?.email?.endsWith('@athlete-era.com') ?? false;
    }, [user?.email]);

    // Content types state
    const [contentTypes, setContentTypes] = useState<ContentTypeWithWorkspace[]>([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(true);
    const [selectedContentType, setSelectedContentType] = useState<ContentTypeWithWorkspace | null>(null);

    // Section configs state
    const [sectionConfigs, setSectionConfigs] = useState<ParsedSectionConfig[]>([]);
    const [isLoadingSectionConfigs, setIsLoadingSectionConfigs] = useState(false);

    // Form state
    const [structureTags, setStructureTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState('');
    const [contentName, setContentName] = useState('');
    const [contentDescription, setContentDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Search modal state
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

    // Knowledge Base modal state
    const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false);

    // Delete confirmation state
    const [deleteRequestId, setDeleteRequestId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Add to library loading state (track by request ID)
    const [addingRequestId, setAddingRequestId] = useState<number | null>(null);
    const [editingRequestId, setEditingRequestId] = useState<number | null>(null);

    // Field options for selection fields (to display labels instead of values)
    const [fieldOptionsMap, setFieldOptionsMap] = useState<FieldOptionsMap>(new Map());
    // Field name map for displaying field names instead of referenceIDs
    const [fieldNameMap, setFieldNameMap] = useState<FieldNameMap>(new Map());

    // Knowledge base state
    const [knowledgeBaseDocCount, setKnowledgeBaseDocCount] = useState<number | null>(null);
    const [isLoadingKnowledgeBase, setIsLoadingKnowledgeBase] = useState(false);

    // Requests state
    const [offset, setOffset] = useState(0);
    const { requests, isLoading: isLoadingRequests, hasMore, reloadRequests, removeRequest } = useAIContentRequests({
        workspaceId: workspaceId || '',
        contentTypeId: selectedContentType?.id,
        limit: 50,
        offset,
    });

    // Reset offset when content type changes
    useEffect(() => {
        setOffset(0);
    }, [selectedContentType?.id]);

    // Auto-reload requests every 10 seconds if there are any in progress
    useEffect(() => {
        // Check if there are any requests that are not in a terminal state
        // Terminal states are: completed, failed, error
        const hasInProgressRequests = requests.some((request) => {
            const status = request.creation_result?.toLowerCase();
            return status !== 'completed' && status !== 'failed' && status !== 'error';
        });

        if (!hasInProgressRequests) {
            return; // No requests in progress, no need to poll
        }

        // Set up interval to reload requests every 10 seconds
        const intervalId = setInterval(() => {
            reloadRequests();
        }, 10000); // 10 seconds

        // Cleanup interval on unmount or when dependencies change
        return () => clearInterval(intervalId);
    }, [requests, reloadRequests]);

    const canCreate = isAdmin && !isCancelled;

    // Load content types for workspace
    useEffect(() => {
        async function loadContentTypes() {
            if (!workspaceId) return;

            setIsLoadingTypes(true);
            try {
                const response = await api.get(`content-types/workspace/${workspaceId}`);
                const types = (response.data.data || []).sort((a: ContentTypeWithWorkspace, b: ContentTypeWithWorkspace) =>
                    a.name.localeCompare(b.name)
                );
                setContentTypes(types);

                // Select first content type by default if available
                if (types.length > 0) {
                    setSelectedContentType(types[0]);
                }
            } catch (err: any) {
                toaster.create({
                    title: err.response?.data?.error || 'Failed to load content types',
                    type: 'error',
                });
            } finally {
                setIsLoadingTypes(false);
            }
        }

        loadContentTypes();
    }, [workspaceId]);

    // Load section configs when content type changes
    useEffect(() => {
        async function loadSectionConfigs() {
            if (!workspaceId || !selectedContentType) {
                setSectionConfigs([]);
                return;
            }

            setIsLoadingSectionConfigs(true);
            try {
                const response = await api.get(
                    `content-types/${selectedContentType.id}/workspace/${workspaceId}/section-configs`
                );
                const configs: SectionConfigField[] = response.data.data || [];
                console.log('Section configs from API:', configs);

                // Parse the sectionconfigs JSON string for each field
                const parsed: ParsedSectionConfig[] = configs.map((config) => {
                    console.log('Config sectionconfigs raw:', config.sectionconfigs);
                    let options: string[] = [];
                    let sectionConfigKey = '';
                    try {
                        const sectionConfigObj = JSON.parse(config.sectionconfigs);
                        // The sectionconfigs is an object with UUID keys and array values
                        // Get the first key and array of options
                        const keys = Object.keys(sectionConfigObj);
                        const values = Object.values(sectionConfigObj);
                        if (keys.length > 0) {
                            sectionConfigKey = keys[0];
                        }
                        if (values.length > 0 && Array.isArray(values[0])) {
                            options = values[0] as string[];
                        }
                    } catch {
                        // If parsing fails, use empty values
                    }
                    console.log('Parsed section_config_key:', sectionConfigKey, 'options:', options);
                    return {
                        field_name: config.field_name,
                        options,
                        section_config_key: sectionConfigKey,
                    };
                });

                console.log('Parsed section configs:', parsed);
                setSectionConfigs(parsed);

                // Get all suggested tags from database (current options from section configs)
                const allSuggestedTags = parsed.length > 0 ? parsed[0].options : [];
                console.log('All suggested tags from database:', allSuggestedTags);

                // Try to restore custom tags from localStorage
                const customTagsKey = `ai-create-tags-${workspaceId}-${selectedContentType.id}`;
                const deselectedTagsKey = `ai-create-deselected-${workspaceId}-${selectedContentType.id}`;

                const savedCustomTags = localStorage.getItem(customTagsKey);
                const savedDeselectedTags = localStorage.getItem(deselectedTagsKey);

                let customTags: string[] = [];
                let deselectedTags: string[] = [];

                // Restore custom tags
                if (savedCustomTags) {
                    try {
                        const parsedCustomTags = JSON.parse(savedCustomTags);
                        if (Array.isArray(parsedCustomTags)) {
                            customTags = parsedCustomTags;
                            console.log('Restored custom tags from localStorage:', customTags);
                        }
                    } catch (err) {
                        console.warn('Failed to parse saved custom tags from localStorage:', err);
                    }
                }

                // Restore deselected suggested tags
                if (savedDeselectedTags) {
                    try {
                        const parsedDeselectedTags = JSON.parse(savedDeselectedTags);
                        if (Array.isArray(parsedDeselectedTags)) {
                            deselectedTags = parsedDeselectedTags;
                            console.log('Restored deselected tags from localStorage:', deselectedTags);
                        }
                    } catch (err) {
                        console.warn('Failed to parse deselected tags from localStorage:', err);
                    }
                }

                // Merge tags:
                // 1. Start with all suggested tags from database
                // 2. Remove any that user has explicitly deselected
                // 3. Add custom tags
                const selectedSuggestedTags = allSuggestedTags.filter(
                    (tag) => !deselectedTags.includes(tag)
                );
                const mergedTags = [...selectedSuggestedTags, ...customTags];
                console.log('Final merged tags:', mergedTags);
                setStructureTags(mergedTags);
            } catch (err: any) {
                console.error('Failed to load section configs:', err);
                setSectionConfigs([]);
                setStructureTags([]);
            } finally {
                setIsLoadingSectionConfigs(false);
            }
        }

        loadSectionConfigs();
    }, [workspaceId, selectedContentType]);

    // Load field options for selection fields (to display labels instead of values)
    useEffect(() => {
        async function loadFieldOptions() {
            if (!workspaceId || !selectedContentType) {
                setFieldOptionsMap(new Map());
                return;
            }

            try {
                const response = await api.get(
                    `content-type-fields/${selectedContentType.id}/${workspaceId}/data`
                );
                const fields = response.data.data || [];

                // Build field-specific options map: referenceID -> (optionValue -> optionLabel)
                const fieldOptsMap: FieldOptionsMap = new Map();
                // Build field name map: referenceID -> field_name
                const nameMap: FieldNameMap = new Map();

                fields.forEach((field: any) => {
                    // Store field name mapping
                    if (field.referenceID && field.field_name) {
                        nameMap.set(field.referenceID, field.field_name);
                    }

                    // Build options map for selection fields
                    if (field.field_type === BlockType.Selection && field.field_options?.length > 0) {
                        const optionsMap = new Map<string, string>();
                        field.field_options.forEach((opt: any) => {
                            optionsMap.set(opt.option_value, opt.option_label);
                        });
                        fieldOptsMap.set(field.referenceID, optionsMap);
                    }
                });

                setFieldOptionsMap(fieldOptsMap);
                setFieldNameMap(nameMap);
            } catch (err) {
                console.error('Failed to load field options:', err);
                setFieldOptionsMap(new Map());
                setFieldNameMap(new Map());
            }
        }

        loadFieldOptions();
    }, [workspaceId, selectedContentType]);

    // Check knowledge base document count
    useEffect(() => {
        async function checkKnowledgeBase() {
            if (!workspaceId || !selectedContentType) {
                setKnowledgeBaseDocCount(null);
                return;
            }

            setIsLoadingKnowledgeBase(true);
            try {
                const url = `/knowledge-base/gemini/${workspaceId}/documents?content_type_id=${selectedContentType.id}`;
                const response = await api.get(url);
                const docs = response.data?.documents || [];
                setKnowledgeBaseDocCount(docs.length);
            } catch (err) {
                console.error('Failed to check knowledge base:', err);
                setKnowledgeBaseDocCount(0);
            } finally {
                setIsLoadingKnowledgeBase(false);
            }
        }

        checkKnowledgeBase();
    }, [workspaceId, selectedContentType]);

    // Callback to refresh knowledge base count (called when modal closes after upload)
    const refreshKnowledgeBaseCount = useCallback(async () => {
        if (!workspaceId || !selectedContentType) return;

        try {
            const url = `/knowledge-base/gemini/${workspaceId}/documents?content_type_id=${selectedContentType.id}`;
            const response = await api.get(url);
            const docs = response.data?.documents || [];
            setKnowledgeBaseDocCount(docs.length);
        } catch (err) {
            console.error('Failed to refresh knowledge base count:', err);
        }
    }, [workspaceId, selectedContentType]);

    // Helper function to save tags to localStorage
    const saveTagsToLocalStorage = useCallback((tags: string[]) => {
        if (!workspaceId || !selectedContentType) return;

        // Extract only custom tags (tags not in current section config suggestions)
        const allSuggestedTags = sectionConfigs.length > 0 ? sectionConfigs[0].options : [];
        const customTagsOnly = tags.filter(
            (tag) => !allSuggestedTags.includes(tag)
        );

        // Find which suggested tags are NOT in tags (user deselected them)
        const deselectedSuggestedTags = allSuggestedTags.filter(
            (tag) => !tags.includes(tag)
        );

        const customTagsKey = `ai-create-tags-${workspaceId}-${selectedContentType.id}`;
        const deselectedTagsKey = `ai-create-deselected-${workspaceId}-${selectedContentType.id}`;

        localStorage.setItem(customTagsKey, JSON.stringify(customTagsOnly));
        localStorage.setItem(deselectedTagsKey, JSON.stringify(deselectedSuggestedTags));

        console.log('Saved custom tags to localStorage:', customTagsOnly);
        console.log('Saved deselected tags to localStorage:', deselectedSuggestedTags);
    }, [workspaceId, selectedContentType, sectionConfigs]);

    // Handle adding a structure tag
    const handleAddTag = () => {
        const trimmedTag = newTag.trim();
        if (trimmedTag && !structureTags.includes(trimmedTag)) {
            const newTags = [...structureTags, trimmedTag];
            setStructureTags(newTags);
            saveTagsToLocalStorage(newTags);
            setNewTag('');
        }
    };

    const handleTagKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        const newTags = structureTags.filter((tag) => tag !== tagToRemove);
        setStructureTags(newTags);
        saveTagsToLocalStorage(newTags);
    };

    // Handle toggling a suggested tag
    const handleToggleTag = (tag: string) => {
        let newTags: string[];
        if (structureTags.includes(tag)) {
            newTags = structureTags.filter((t) => t !== tag);
        } else {
            newTags = [...structureTags, tag];
        }
        setStructureTags(newTags);
        saveTagsToLocalStorage(newTags);
    };

    // Handle content selection from search modal
    const handleContentSelect = (content: { name: string; description: string }) => {
        setContentName(content.name);
        setContentDescription(content.description);
        setIsSearchModalOpen(false);
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!workspaceId || !selectedContentType) {
            toaster.create({
                title: 'Please select a content type',
                type: 'error',
            });
            return;
        }

        if (!contentName.trim()) {
            toaster.create({
                title: 'Please enter a content name',
                type: 'error',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            // Build section_configs object with the original sectionconfig key and structureTags as value
            // Can be empty if no section configs are available
            console.log('Submit - sectionConfigs state:', sectionConfigs);
            console.log('Submit - structureTags:', structureTags);
            const sectionConfigsPayload: Record<string, string[]> = {};
            if (sectionConfigs.length > 0 && sectionConfigs[0].section_config_key) {
                sectionConfigsPayload[sectionConfigs[0].section_config_key] = structureTags;
            }
            console.log('Submit - sectionConfigsPayload:', sectionConfigsPayload);

            await api.post('/ai-content-requests', {
                workspace_id: parseInt(workspaceId),
                content_type_id: selectedContentType.id,
                name: contentName.trim(),
                description: contentDescription.trim(),
                country: workspace?.country || '',
                sport_type: getSportName(workspace?.sport_type_id || 0),
                section_configs: Object.keys(sectionConfigsPayload).length > 0 ? sectionConfigsPayload : null,
                assistant_name: 'genai',
            });

            toaster.create({
                title: 'Content creation request submitted!',
                type: 'success',
            });

            // Reset form
            setContentName('');
            setContentDescription('');

            // Reload requests
            reloadRequests();
        } catch (err: any) {
            toaster.create({
                title: err.response?.data?.error || 'Failed to create content request',
                type: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAdd = async (request: AIContentRequest) => {
        setAddingRequestId(request.id);
        try {
            await api.post(`/ai-content-requests/${request.id}/workspace-content`);
            // Delete the AI content request after successful workspace content creation
            await api.delete(`/ai-content-requests/${request.id}`);
            toaster.create({
                title: 'Content added to library successfully',
                type: 'success',
            });
            // Immediately remove from local state
            removeRequest(request.id);
            // Also reload to ensure sync with backend
            setOffset(0);
            reloadRequests();
        } catch (err: any) {
            toaster.create({
                title: err.response?.data?.error || 'Failed to add content to library',
                type: 'error',
            });
        } finally {
            setAddingRequestId(null);
        }
    };

    const handleEdit = async (request: AIContentRequest) => {
        setEditingRequestId(request.id);
        try {
            // First add to library
            const response = await api.post(`/ai-content-requests/${request.id}/workspace-content`);
            // Extract workspace content ID from response
            const workspaceContentId = response.data?.data?.id || response.data?.id;

            if (!workspaceContentId) {
                throw new Error('Workspace content ID not found in response');
            }

            // Delete the AI content request after successful workspace content creation
            await api.delete(`/ai-content-requests/${request.id}`);
            // Immediately remove from local state
            removeRequest(request.id);
            // Also reload to ensure sync with backend
            setOffset(0);
            reloadRequests();
            // Then navigate to edit view using workspace content ID
            navigate(`/${workspaceId}/content-types/${request.content_type_id}/contents/${workspaceContentId}/edit`);
        } catch (err: any) {
            toaster.create({
                title: err.response?.data?.error || 'Failed to add content to library',
                type: 'error',
            });
            setEditingRequestId(null);
        }
    };

    const handleDelete = (requestId: number) => {
        setDeleteRequestId(requestId);
    };

    const confirmDelete = async () => {
        if (!deleteRequestId) return;

        setIsDeleting(true);
        try {
            await api.delete(`/ai-content-requests/${deleteRequestId}`);
            toaster.create({
                title: 'Content creation request deleted successfully',
                type: 'success',
            });
            setDeleteRequestId(null);
            reloadRequests();
            // Reset offset to show updated list
            setOffset(0);
        } catch (err: any) {
            toaster.create({
                title: err.response?.data?.error || 'Failed to delete content creation request',
                type: 'error',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleLoadMore = () => {
        if (hasMore && !isLoadingRequests) {
            setOffset((prev) => prev + 50);
        }
    };

    if (isLoadingTypes) {
        return (
            <Card.Root minH="100vh" mx="auto" width={{ md: '90%' }} maxW="4xl">
                <Card.Body>
                    <Flex justify="center" align="center" minH="400px">
                        <Spinner size="xl" colorPalette="brand" />
                    </Flex>
                </Card.Body>
            </Card.Root>
        );
    }

    return (
        <Card.Root minH="100vh" mx="auto" width={{ md: '90%' }} maxW="4xl">
            <Card.Header textAlign="center" pb={4}>
                <Heading size={{ base: 'xl', md: '2xl' }} mb={2}>
                    Let's create content for your libraries.
                </Heading>
                <Text color="gray.600">What type of content would you like to create?</Text>
            </Card.Header>

            <Card.Body>
                <Container maxW="3xl">
                    <Stack gap={8}>
                        {/* Content Type Selection */}
                        <Flex justify="center" gap={3} wrap="wrap">
                            {contentTypes.map((type) => (
                                <Button
                                    key={type.id}
                                    variant={selectedContentType?.id === type.id ? 'solid' : 'outline'}
                                    colorPalette={selectedContentType?.id === type.id ? 'brand' : 'gray'}
                                    size="lg"
                                    px={8}
                                    onClick={() => setSelectedContentType(type)}
                                >
                                    {type.name}
                                </Button>
                            ))}
                        </Flex>

                        {selectedContentType && (
                            <Stack gap={6}>
                                {/* Knowledge Base Status */}
                                {isLoadingKnowledgeBase ? (
                                    <Flex justify="center" py={4}>
                                        <Spinner size="sm" colorPalette="brand" />
                                    </Flex>
                                ) : knowledgeBaseDocCount === 0 ? (
                                    <Box
                                        p={6}
                                        bg="orange.50"
                                        border="1px solid"
                                        borderColor="orange.200"
                                        borderRadius="lg"
                                        textAlign="center"
                                    >
                                        <Icon as={BookOpenIcon} w={10} h={10} color="orange.500" mb={3} />
                                        <Heading size="md" mb={2} color="orange.700">
                                            Knowledge Base Required
                                        </Heading>
                                        <Text color="orange.600" mb={4}>
                                            Please upload documents to your knowledge base before creating content.
                                            The AI uses these documents to generate accurate and relevant content for your {selectedContentType.name.toLowerCase()}.
                                        </Text>
                                        <Button
                                            colorPalette="orange"
                                            onClick={() => setIsKnowledgeBaseModalOpen(true)}
                                        >
                                            <Icon as={BookOpenIcon} w={4} h={4} />
                                            Upload to Knowledge Base
                                        </Button>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Knowledge Base Button */}
                                        <Flex justify="flex-end" align="center" gap={2}>
                                            <Badge colorPalette="green" size="sm">
                                                {knowledgeBaseDocCount} document{knowledgeBaseDocCount !== 1 ? 's' : ''} in knowledge base
                                            </Badge>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setIsKnowledgeBaseModalOpen(true)}
                                            >
                                                <Icon as={BookOpenIcon} w={4} h={4} />
                                                Manage
                                            </Button>
                                        </Flex>

                                        {/* Content Name */}
                                        <Box>
                                            <Flex justify="space-between" align="center" mb={2}>
                                                <Text fontWeight="semibold">{selectedContentType.name} Name</Text>
                                            </Flex>
                                            <Flex gap={2}>
                                                <Input
                                                    value={contentName}
                                                    onChange={(e) => setContentName(e.target.value)}
                                                    placeholder={`Enter the name of the ${selectedContentType.name.toLowerCase()} here or find a ${selectedContentType.name.toLowerCase()} name from your knowledge base`}
                                                    flex={1}
                                                />
                                                <Tooltip content="Search knowledge base">
                                                    <IconButton
                                                        variant="outline"
                                                        onClick={() => setIsSearchModalOpen(true)}
                                                        aria-label="Search knowledge base"
                                                    >
                                                        <Icon as={MagnifyingGlassIcon} w={5} h={5} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Flex>
                                        </Box>

                                        {/* Content Description */}
                                        <Field label={`${selectedContentType.name} Description`}>
                                            <Textarea
                                                value={contentDescription}
                                                onChange={(e) => setContentDescription(e.target.value)}
                                                placeholder={`Enter a short description of the ${selectedContentType.name.toLowerCase()} you would like to create`}
                                                rows={4}
                                            />
                                        </Field>

                                        {/* Section Structure */}
                                        {isLoadingSectionConfigs ? (
                                            <Flex justify="center" py={4}>
                                                <Spinner size="sm" colorPalette="brand" />
                                            </Flex>
                                        ) : (
                                            <>
                                                {/* Suggested tags from section configs */}
                                                {sectionConfigs.map((config) => (
                                                    <Box key={config.field_name}>
                                                        <Text fontWeight="semibold" mb={2}>
                                                            {config.field_name} Structure
                                                        </Text>
                                                        <Text color="gray.500" fontSize="sm" mb={3}>
                                                            What information do you want included in each {config.field_name.toLowerCase()}?
                                                        </Text>
                                                        <HStack gap={2} wrap="wrap" mb={3}>
                                                            {config.options.map((option) => {
                                                                const isSelected = structureTags.includes(option);
                                                                return (
                                                                    <Tag.Root
                                                                        key={option}
                                                                        size="lg"
                                                                        colorPalette={isSelected ? 'brand' : 'gray'}
                                                                        variant={isSelected ? 'solid' : 'outline'}
                                                                        cursor="pointer"
                                                                        onClick={() => handleToggleTag(option)}
                                                                        _hover={{
                                                                            opacity: 0.8,
                                                                        }}
                                                                    >
                                                                        <Tag.Label>{option}</Tag.Label>
                                                                    </Tag.Root>
                                                                );
                                                            })}
                                                            {/* Custom tags (tags not in suggestions) */}
                                                            {structureTags
                                                                .filter((tag) => !sectionConfigs.some((c) => c.options.includes(tag)))
                                                                .map((tag) => (
                                                                    <Tag.Root key={tag} size="lg" colorPalette="brand" variant="solid">
                                                                        <Tag.Label>{tag}</Tag.Label>
                                                                        <Tag.EndElement>
                                                                            <Tag.CloseTrigger onClick={() => handleRemoveTag(tag)} />
                                                                        </Tag.EndElement>
                                                                    </Tag.Root>
                                                                ))}
                                                        </HStack>
                                                        {/* Add custom tag input */}
                                                        <Flex gap={2}>
                                                            <Input
                                                                value={newTag}
                                                                onChange={(e) => setNewTag(e.target.value)}
                                                                onKeyDown={handleTagKeyDown}
                                                                placeholder="Type and press Enter or click + to add..."
                                                                flex={1}
                                                            />
                                                            <Button variant="outline" onClick={handleAddTag} disabled={!newTag.trim()}>
                                                                <Icon as={PlusIcon} w={4} h={4} />
                                                                Add
                                                            </Button>
                                                        </Flex>
                                                    </Box>
                                                ))}
                                            </>
                                        )}

                                        {/* Submit Button */}
                                        {canCreate && (
                                            <Flex justify="center">
                                                <Button
                                                    colorPalette="brand"
                                                    size="lg"
                                                    px={12}
                                                    onClick={handleSubmit}
                                                    loading={isSubmitting}
                                                    disabled={!contentName.trim()}
                                                >
                                                    Create
                                                </Button>
                                            </Flex>
                                        )}
                                    </>
                                )}
                            </Stack>
                        )}

                        {/* Content Creation Requests Table */}
                        <Box mt={10}>
                            <Flex justify="space-between" align="center" mb={6}>
                                <Heading size="xl" fontWeight="bold">
                                    Content Creation Requests
                                </Heading>
                                {requests.length > 0 && (
                                    <Badge colorPalette="gray" variant="subtle" size="lg">
                                        {requests.length} {requests.length === 1 ? 'request' : 'requests'}
                                    </Badge>
                                )}
                            </Flex>

                            {/* Desktop Table */}
                            <Box display={{ base: 'none', md: 'block' }}>
                                <Card.Root>
                                    <Card.Body p={0}>
                                        <Table.Root>
                                            <Table.Header>
                                                <Table.Row bg="gray.50">
                                                    <Table.ColumnHeader color="gray.700" fontWeight="semibold" py={3} px={4}>
                                                        Name
                                                    </Table.ColumnHeader>
                                                    <Table.ColumnHeader color="gray.700" fontWeight="semibold" py={3} px={4} width="180px">
                                                        Status & Date
                                                    </Table.ColumnHeader>
                                                    <Table.ColumnHeader color="gray.700" fontWeight="semibold" py={3} px={4} width="100px" textAlign="right">
                                                        Actions
                                                    </Table.ColumnHeader>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {isLoadingRequests && offset === 0 ? (
                                                    Array.from({ length: 3 }).map((_, index) => (
                                                        <Table.Row key={index}>
                                                            <Table.Cell colSpan={3} py={3} px={4}>
                                                                <Flex gap={3} align="center">
                                                                    <Box h="16px" w="200px" bg="gray.100" borderRadius="md" />
                                                                    <Box h="16px" w="150px" bg="gray.100" borderRadius="md" />
                                                                </Flex>
                                                            </Table.Cell>
                                                        </Table.Row>
                                                    ))
                                                ) : requests.length === 0 ? (
                                                    <Table.Row>
                                                        <Table.Cell colSpan={3} py={12} px={6}>
                                                            <Flex direction="column" align="center" gap={3}>
                                                                <Icon as={DocumentIcon} w={12} h={12} color="gray.300" />
                                                                <Text color="gray.500" fontSize="md" fontWeight="medium">
                                                                    No content creation requests yet
                                                                </Text>
                                                                <Text color="gray.400" fontSize="sm">
                                                                    Create your first content request above
                                                                </Text>
                                                            </Flex>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                ) : (
                                                    requests.map((request) => (
                                                        <RequestTableRow
                                                            key={request.id}
                                                            request={request}
                                                            onDelete={handleDelete}
                                                            onAdd={handleAdd}
                                                            onEdit={handleEdit}
                                                            isAdding={addingRequestId === request.id}
                                                            isEditing={editingRequestId === request.id}
                                                            fieldOptionsMap={fieldOptionsMap}
                                                            fieldNameMap={fieldNameMap}
                                                            showSources={showSources}
                                                        />
                                                    ))
                                                )}
                                            </Table.Body>
                                        </Table.Root>
                                    </Card.Body>
                                </Card.Root>
                            </Box>

                            {/* Mobile Cards */}
                            <Box display={{ base: 'block', md: 'none' }}>
                                <Stack gap={4}>
                                    {isLoadingRequests && offset === 0 ? (
                                        Array.from({ length: 3 }).map((_, index) => (
                                            <Card.Root key={index}>
                                                <Card.Body p={5}>
                                                    <Stack gap={3}>
                                                        <Box h="20px" w="60%" bg="gray.100" borderRadius="md" />
                                                        <Box h="16px" w="40%" bg="gray.100" borderRadius="md" />
                                                        <Box h="16px" w="80%" bg="gray.100" borderRadius="md" />
                                                    </Stack>
                                                </Card.Body>
                                            </Card.Root>
                                        ))
                                    ) : requests.length === 0 ? (
                                        <Card.Root>
                                            <Card.Body p={12}>
                                                <Flex direction="column" align="center" gap={3}>
                                                    <Icon as={DocumentIcon} w={12} h={12} color="gray.300" />
                                                    <Text color="gray.500" fontSize="md" fontWeight="medium">
                                                        No content creation requests yet
                                                    </Text>
                                                    <Text color="gray.400" fontSize="sm" textAlign="center">
                                                        Create your first content request above
                                                    </Text>
                                                </Flex>
                                            </Card.Body>
                                        </Card.Root>
                                    ) : (
                                        requests.map((request) => (
                                            <MobileRequestCard
                                                key={request.id}
                                                request={request}
                                                onDelete={handleDelete}
                                                onAdd={handleAdd}
                                                onEdit={handleEdit}
                                                isAdding={addingRequestId === request.id}
                                                isEditing={editingRequestId === request.id}
                                                fieldOptionsMap={fieldOptionsMap}
                                                fieldNameMap={fieldNameMap}
                                                showSources={showSources}
                                            />
                                        ))
                                    )}
                                </Stack>
                            </Box>

                            {/* Load More */}
                            {hasMore && !isLoadingRequests && requests.length > 0 && (
                                <Flex justify="center" mt={6}>
                                    <Button variant="outline" colorPalette="brand" onClick={handleLoadMore}>
                                        Load More
                                    </Button>
                                </Flex>
                            )}

                            {isLoadingRequests && offset > 0 && (
                                <Flex justify="center" mt={4}>
                                    <Spinner size="sm" colorPalette="brand" />
                                </Flex>
                            )}
                        </Box>
                    </Stack>
                </Container>
            </Card.Body>

            {/* Search Modal */}
            <ContentSearchModal
                isOpen={isSearchModalOpen}
                onClose={() => setIsSearchModalOpen(false)}
                workspaceId={workspaceId || ''}
                contentTypeId={selectedContentType?.id?.toString() || ''}
                contentTypeName={selectedContentType?.name || 'Content'}
                onSelect={handleContentSelect}
            />

            {/* Knowledge Base Modal */}
            {selectedContentType && (
                <KnowledgeBaseModal
                    isOpen={isKnowledgeBaseModalOpen}
                    onClose={() => setIsKnowledgeBaseModalOpen(false)}
                    workspaceId={workspaceId || ''}
                    contentTypeId={selectedContentType.id}
                    contentTypeName={selectedContentType.name}
                    canModify={isAdmin && !isCancelled}
                    onDocumentsChange={refreshKnowledgeBaseCount}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DestructiveModal
                isOpen={!!deleteRequestId}
                onCancel={() => setDeleteRequestId(null)}
                onConfirm={confirmDelete}
                isLoading={isDeleting}
                title="Delete Content Creation Request"
                confirmText="Delete"
            >
                <Text>
                    Are you sure you want to delete this content creation request? This action cannot be undone.
                </Text>
            </DestructiveModal>
        </Card.Root >
    );
}

